import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageCircle, Loader2, ChevronLeft, LogOut, CheckCircle2, Lock } from 'lucide-react';
import { api } from '../lib/api';
import DoubtChatView from '../components/DoubtChatView';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const storageKey = (slug) => `doubt_engine_session_${slug}`;

export default function PublicDoubtEngine() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState(null);
  const [session, setSession] = useState(null); // {token, phone, name}
  const [chats, setChats] = useState([]);
  const [openChat, setOpenChat] = useState(null); // {chat, messages}
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState('list'); // 'list' | 'teacher-picker' | 'chat'
  const [starting, setStarting] = useState(false);
  const [closing, setClosing] = useState(false);

  const handleCloseChat = async () => {
    if (!openChat || closing) return;
    const ok = window.confirm(
      "Close this chat? Once closed you won't be able to send or receive any new messages here. You can always start a new doubt with the teacher."
    );
    if (!ok) return;
    setClosing(true);
    try {
      await api.closePublicDoubtChat(session.token, openChat.chat.id);
      toast.success('Chat closed. Thanks for using the Doubt Engine!');
      // Refresh chat list so it reflects the closed status, then return to list view
      try {
        const list = await api.listPublicDoubtChats(session.token);
        setChats(list.data || []);
      } catch (_) { /* ignore */ }
      setOpenChat(null);
      setView('list');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not close chat');
    } finally {
      setClosing(false);
    }
  };

  // Load batch info + restore session
  useEffect(() => {
    (async () => {
      try {
        const res = await api.getPublicDoubtBatch(slug);
        setBatch(res.data);
      } catch (e) {
        setBatch({ __error: e?.response?.data?.detail || 'This link is not active.' });
      } finally { setLoading(false); }
    })();
    const stored = localStorage.getItem(storageKey(slug));
    if (stored) {
      try { setSession(JSON.parse(stored)); } catch (e) { /* noop */ }
    }
  }, [slug]);

  // When session available, fetch student's chats
  useEffect(() => {
    if (!session?.token) return;
    (async () => {
      try {
        const res = await api.listPublicDoubtChats(session.token);
        setChats(res.data || []);
      } catch (e) {
        if (e?.response?.status === 401) {
          localStorage.removeItem(storageKey(slug));
          setSession(null);
        }
      }
    })();
  }, [session, slug]);

  const startSession = async () => {
    const cleanPhone = phone.replace(/\D/g, '');
    if (cleanPhone.length < 10) { toast.error('Enter a valid 10-digit phone'); return; }
    if (!name.trim()) { toast.error('Enter your name'); return; }
    setCreating(true);
    try {
      const res = await api.createDoubtStudentSession({ name: name.trim(), phone: cleanPhone });
      const s = res.data;
      localStorage.setItem(storageKey(slug), JSON.stringify(s));
      setSession(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start session');
    } finally { setCreating(false); }
  };

  const logout = () => {
    localStorage.removeItem(storageKey(slug));
    setSession(null);
    setChats([]);
    setOpenChat(null);
    setView('list');
  };

  const pickTeacher = async (teacherId) => {
    setStarting(true);
    try {
      const startRes = await api.startPublicDoubtChat(session.token, slug, teacherId);
      const chat = startRes.data;
      const msgs = await api.getPublicDoubtMessages(session.token, chat.id);
      setOpenChat({ chat, messages: msgs.data.messages || [] });
      setView('chat');
      // Refresh sidebar list
      const list = await api.listPublicDoubtChats(session.token);
      setChats(list.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start chat');
    } finally { setStarting(false); }
  };

  const openExistingChat = async (chat) => {
    try {
      const res = await api.getPublicDoubtMessages(session.token, chat.id);
      setOpenChat({ chat, messages: res.data.messages || [] });
      setView('chat');
      setChats((cs) => cs.map((c) => (c.id === chat.id ? { ...c, student_unread_count: 0 } : c)));
    } catch (e) {
      toast.error('Cannot open chat');
    }
  };

  const wsUrl = (chatId) => {
    if (!session?.token || !chatId) return null;
    const base = BACKEND_URL.replace(/^http/, 'ws');
    return `${base}/api/doubt-engine/ws/${chatId}?token=${encodeURIComponent(session.token)}&role=student`;
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300"><Loader2 className="animate-spin" /></div>;
  }
  if (batch?.__error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300 p-4">
        <div className="max-w-md text-center">
          <MessageCircle size={48} className="mx-auto mb-3 text-slate-500" />
          <h1 className="text-xl font-semibold text-white">Doubt Engine unavailable</h1>
          <p className="text-sm text-slate-400 mt-2">{batch.__error}</p>
        </div>
      </div>
    );
  }

  // Not signed in — show phone+name form
  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-slate-950 to-indigo-950 text-slate-100 flex items-center justify-center p-4" data-testid="public-doubt-onboard">
        <div className="w-full max-w-md bg-slate-900/70 border border-slate-700 rounded-2xl p-6 backdrop-blur">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
              <MessageCircle className="text-emerald-300" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Doubt Engine</h1>
              <p className="text-xs text-slate-400 truncate max-w-[240px]">{batch?.course_name}</p>
            </div>
          </div>
          <p className="text-sm text-slate-300 mb-5">Enter your details to start chatting with your teachers.</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Your name</label>
              <input value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="e.g. Rohit Kumar"
                     className="w-full bg-slate-800/70 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                     data-testid="public-doubt-name" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Mobile number</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)}
                     placeholder="10-digit mobile"
                     inputMode="numeric"
                     className="w-full bg-slate-800/70 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                     data-testid="public-doubt-phone" />
            </div>
            <button onClick={startSession} disabled={creating}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg font-medium disabled:opacity-60"
                    data-testid="public-doubt-start-btn">
              {creating ? <Loader2 className="animate-spin" size={18} /> : null}
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Chat view
  if (view === 'chat' && openChat) {
    const isClosed = openChat.chat?.status === 'closed';
    return (
      <div className="h-[100dvh] bg-slate-950 flex flex-col overflow-hidden" data-testid="public-doubt-chat">
        <div className="px-3 py-2 bg-slate-900/90 border-b border-slate-700 flex items-center gap-2 shrink-0">
          <button onClick={() => { setView('list'); setOpenChat(null); }} className="p-2 text-slate-300 hover:text-white">
            <ChevronLeft size={20} />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-100 truncate">{openChat.chat.teacher_name}</div>
            <div className="text-[11px] text-slate-400 truncate">{openChat.chat.course_name}</div>
          </div>
          {!isClosed && (
            <button
              onClick={handleCloseChat}
              disabled={closing}
              data-testid="public-doubt-close-btn"
              title="Mark this doubt as resolved and close the chat"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium rounded-md bg-emerald-600/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-600/30 disabled:opacity-60"
            >
              {closing ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
              <span>Close</span>
            </button>
          )}
        </div>
        {isClosed && (
          <div className="px-3 py-2 bg-emerald-900/40 border-b border-emerald-700/50 text-emerald-200 text-[11px] flex items-center gap-2" data-testid="public-doubt-closed-banner">
            <Lock size={12} />
            <span>This chat has been closed{openChat.chat?.closed_at ? ` on ${new Date(openChat.chat.closed_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}. Start a new doubt if you have another question.</span>
          </div>
        )}
        <div className="flex-1 min-h-0">
          <DoubtChatView
            chatId={openChat.chat.id}
            wsUrl={wsUrl(openChat.chat.id)}
            identity="student"
            initialMessages={openChat.messages}
            header={null}
            readOnly={isClosed}
            disabled={isClosed}
            onClosed={(updated) => {
              setOpenChat((prev) => prev ? { ...prev, chat: { ...prev.chat, ...(updated || {}), status: 'closed' } } : prev);
              setChats((cs) => cs.map((c) => (c.id === openChat.chat.id ? { ...c, status: 'closed' } : c)));
            }}
            onSendText={async (content) => (await api.sendPublicDoubtText(session.token, openChat.chat.id, content)).data}
            onSendMedia={async (file, mt) => (await api.sendPublicDoubtMedia(session.token, openChat.chat.id, file, mt)).data}
          />
        </div>
      </div>
    );
  }

  // Teacher picker
  if (view === 'teacher-picker') {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 p-4" data-testid="public-doubt-picker">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <button onClick={() => setView('list')} className="p-2 hover:bg-slate-800 rounded"><ChevronLeft size={20} /></button>
            <h2 className="font-semibold text-lg">Start a new doubt</h2>
          </div>
          <p className="text-sm text-slate-400 mb-3">Choose a teacher to chat with:</p>
          <div className="space-y-2">
            {(batch?.teachers || []).length === 0 && (
              <div className="text-sm text-slate-500 p-6 text-center bg-slate-900 rounded-xl border border-slate-800">
                No teachers assigned yet. Please contact your admin.
              </div>
            )}
            {(batch?.teachers || []).map((t) => (
              <button key={t.id} onClick={() => pickTeacher(t.id)} disabled={starting}
                      data-testid={`public-doubt-pick-${t.id}`}
                      className="w-full flex items-center gap-3 p-3 bg-slate-900/70 hover:bg-slate-800 border border-slate-800 rounded-xl transition disabled:opacity-60">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-300 font-bold">
                  {(t.name || '?')[0]?.toUpperCase()}
                </div>
                <div className="text-left flex-1">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-[11px] text-slate-500">Tap to start a chat</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Default: chat list view
  return (
    <div className="h-[100dvh] bg-slate-950 text-slate-100 flex flex-col overflow-hidden" data-testid="public-doubt-list">
      <div className="px-4 py-3 bg-slate-900/90 border-b border-slate-800 flex items-center justify-between shrink-0">
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{batch?.course_name}</div>
          <div className="text-[11px] text-slate-400 truncate">Signed in as {session.name} · {session.phone}</div>
        </div>
        <button onClick={logout} className="p-2 text-slate-400 hover:text-white" title="Logout"><LogOut size={18} /></button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {chats.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <MessageCircle size={48} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">No chats yet. Start a new one with a teacher.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-800">
            {chats.map((c) => (
              <li key={c.id}>
                <button
                  onClick={() => openExistingChat(c)}
                  data-testid={`public-doubt-open-${c.id}`}
                  className="w-full text-left p-4 hover:bg-slate-900 flex items-start gap-3"
                >
                  <div className="w-11 h-11 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-300 font-bold shrink-0">
                    {(c.teacher_name || '?')[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate flex items-center gap-1.5">
                        {c.teacher_name}
                        {c.status === 'closed' && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                            <Lock size={9} /> Closed
                          </span>
                        )}
                      </span>
                      <span className="text-[10px] text-slate-500 whitespace-nowrap">
                        {c.last_message_at ? new Date(c.last_message_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : ''}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 truncate">{c.last_message_preview || 'No messages yet'}</div>
                  </div>
                  {c.student_unread_count > 0 && (
                    <span className="min-w-[22px] h-5 px-1.5 flex items-center justify-center rounded-full bg-emerald-600 text-white text-[10px] font-bold self-center">
                      {c.student_unread_count}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="p-3 border-t border-slate-800 bg-slate-900/80">
        <button
          onClick={() => setView('teacher-picker')}
          data-testid="public-doubt-new-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg font-medium"
        >
          <MessageCircle size={18} /> Start a new doubt
        </button>
      </div>
    </div>
  );
}
